// src/components/admin_panel/AdminConnectionsAnalytics.js
import React, { useState, useEffect } from "react";

const StatCard = ({ label, value, gradient, textColor = "white" }) => (
  <div style={{
    background: gradient,
    color: textColor,
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.12)"
  }}>
    <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>{label}</div>
    <div style={{ fontSize: "32px", fontWeight: "700" }}>{value}</div>
  </div>
);

const AdminConnectionsAnalytics = ({ adminToken }) => {
  const [analytics, setAnalytics] = useState(null);
  const [cadenceAnalytics, setCadenceAnalytics] = useState(null);
  const [wishlistAnalytics, setWishlistAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchJson = async (url) => {
      const response = await fetch(url, { headers: { Authorization: `Bearer ${adminToken}` } });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    };

    const fetchAnalytics = async () => {
      if (!adminToken) return;
      setLoading(true);
      setError("");
      try {
        const [connectionsData, cadenceData, wishlistData] = await Promise.all([
          fetchJson("/api/admin/connections-analytics"),
          fetchJson("/api/admin/cadence-reminder-analytics"),
          fetchJson("/api/admin/wishlist-analytics"),
        ]);
        setAnalytics(connectionsData);
        setCadenceAnalytics(cadenceData);
        setWishlistAnalytics(wishlistData);
      } catch (err) {
        console.error("Error fetching connections analytics:", err);
        setError("Failed to load connections analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [adminToken]);

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div style={{ fontSize: "18px", color: "#666" }}>Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "40px" }}>
        <div style={{ background: "#fee2e2", border: "1px solid #ef4444", borderRadius: "8px", padding: "16px", color: "#991b1b" }}>
          {error}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div style={{ fontSize: "18px", color: "#666" }}>No data available</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ marginBottom: "10px", color: "#111827" }}>
        🤝 Connections &amp; Self-Run Gatherings
      </h2>
      <p style={{ color: "#6B7280", marginBottom: "30px", maxWidth: "700px" }}>
        Adoption of connections/circles/self-run events, and how many guest RSVPs (people with
        no OkGather account at the time) came through public self-run event links, versus how
        many of those guests later signed up.
      </p>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "20px",
        marginBottom: "30px"
      }}>
        <StatCard label="Connections Made" value={analytics.total_connections} gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" />
        <StatCard label="Accepted Connections" value={analytics.accepted_connections} gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" />
        <StatCard label="Circles Created" value={analytics.total_circles} gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" textColor="#111827" />
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "20px",
        marginBottom: "30px"
      }}>
        <StatCard label="Self-Run Events (Total)" value={analytics.total_self_run_events} gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)" textColor="#111827" />
        <StatCard label="Public Events" value={analytics.public_self_run_events} gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" />
        <StatCard label="Private Events" value={analytics.private_self_run_events} gradient="linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)" textColor="#111827" />
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "20px",
        marginBottom: "30px"
      }}>
        <StatCard label="Guest RSVPs (No Account)" value={analytics.guest_rsvps} gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" />
        <StatCard label="Member RSVPs" value={analytics.member_rsvps} gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" />
        <StatCard
          label="Guests Who Later Signed Up"
          value={`${analytics.guest_emails_that_later_signed_up} / ${analytics.unique_guest_emails} (${analytics.guest_to_signup_rate_percent.toFixed(1)}%)`}
          gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
          textColor="#111827"
        />
      </div>

      <p style={{ color: "#9CA3AF", fontSize: "0.85rem", maxWidth: "700px", marginBottom: "30px" }}>
        Worth watching over time: a growing share of guests who RSVP repeatedly to public
        self-run events but never appear in the signup-conversion number above is the
        cannibalization signal to look out for, people getting enough value from the free
        loop that they never subscribe.
      </p>

      {cadenceAnalytics && (
        <>
          <h3 style={{ marginBottom: "10px", color: "#111827" }}>⏰ Stay In Touch</h3>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            marginBottom: "30px"
          }}>
            <StatCard label="Reminders Set" value={cadenceAnalytics.total_cadence_reminders} gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" />
            <StatCard label="Currently Overdue" value={cadenceAnalytics.currently_overdue} gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)" textColor="#111827" />
            <StatCard label="Reminder Emails Sent" value={cadenceAnalytics.reminder_emails_sent} gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" />
          </div>
        </>
      )}

      {wishlistAnalytics && (
        <>
          <h3 style={{ marginBottom: "10px", color: "#111827" }}>💭 Wishlist</h3>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            marginBottom: "10px"
          }}>
            <StatCard label="Ideas Saved" value={wishlistAnalytics.total_wishlist_items} gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" textColor="#111827" />
            <StatCard label="Public Ideas" value={wishlistAnalytics.public_wishlist_items} gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" />
            <StatCard label="Private Shares" value={wishlistAnalytics.total_shares} gradient="linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)" textColor="#111827" />
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            marginBottom: "30px"
          }}>
            <StatCard label="Total Interest ('I'm in')" value={wishlistAnalytics.total_interests} gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" />
            <StatCard label="Guest Interest (No Account)" value={wishlistAnalytics.guest_interests} gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" />
            <StatCard label="Guests Who Later Signed Up" value={wishlistAnalytics.guest_emails_that_later_signed_up} gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" textColor="#111827" />
          </div>
        </>
      )}
    </div>
  );
};

export default AdminConnectionsAnalytics;
